import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { CRMListObjectsType } from "app/pages/sales-intelligence/sub-modules/management/types";
import { selectIsCrmListEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmSettingsSelectors";
import {
    setHsCrmListObjectsTypeThunk,
    setIsHsCrmListEnabledThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/toggles";
import {
    setIsSfCrmListEnabledThunk,
    setSfCrmListObjectsTypeThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    setIsDynamicsCrmListEnabledThunk,
    setDynamicsCrmListObjectsTypeThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/toggles";
import { IntegrationSetting } from "../IntegrationSetting";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import { selectSfListObjectsType } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { selectDynamicsListObjectsType } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import { ConfirmDisableModal } from "./ConfirmDisableModal";
import { getCrmListObjectsTypes, getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { makeCrmSyncTrackingService } from "./crmSyncTrackingService";
import { selectHsListObjectsType } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";

const config = {
    salesforce: {
        options: [
            {
                value: CRMListObjectsType.AccountsAndLeads,
                get label() {
                    return translate("si.common.accounts-and-leads");
                },
            },
            {
                value: CRMListObjectsType.Accounts,
                get label() {
                    return translate("si.common.accounts");
                },
            },
            {
                value: CRMListObjectsType.Leads,
                get label() {
                    return translate("si.common.leads");
                },
            },
        ],
        setCrmListObjectsTypeThunk: setSfCrmListObjectsTypeThunk,
        setIsCrmListEnabledThunk: setIsSfCrmListEnabledThunk,
        selectCrmListObjectsType: selectSfListObjectsType,
    },
    hubspot: {
        options: [
            {
                value: CRMListObjectsType.AccountsAndContacts,
                get label() {
                    return translate("si.common.companies-and-contacts");
                },
            },
            {
                value: CRMListObjectsType.Accounts,
                get label() {
                    return translate("si.common.companies");
                },
            },
            {
                value: CRMListObjectsType.Contacts,
                get label() {
                    return translate("si.common.contacts");
                },
            },
        ],
        setCrmListObjectsTypeThunk: setHsCrmListObjectsTypeThunk,
        setIsCrmListEnabledThunk: setIsHsCrmListEnabledThunk,
        selectCrmListObjectsType: selectHsListObjectsType,
    },
    dynamics: {
        options: [
            {
                value: CRMListObjectsType.AccountsAndLeads,
                label: translate("si.common.accounts-and-leads"),
            },
            { value: CRMListObjectsType.Accounts, label: translate("si.common.accounts") },
            { value: CRMListObjectsType.Leads, label: translate("si.common.leads") },
        ],
        setCrmListObjectsTypeThunk: setDynamicsCrmListObjectsTypeThunk,
        setIsCrmListEnabledThunk: setIsDynamicsCrmListEnabledThunk,
        selectCrmListObjectsType: selectDynamicsListObjectsType,
    },
};

export const CrmSyncSettings = () => {
    const [isConfirmDisableModalOpen, setIsConfirmDisableModalOpen] = useState(false);
    const dispatch = useDispatch();

    const { integrationName: crm } = getIntegrationNameFromUrl();
    const isDynamics = crm === "dynamics";

    const isEnabled = useSelector((state) => selectIsCrmListEnabled(state, crm as Integration));
    const objectsType = useSelector(config[crm as Integration].selectCrmListObjectsType);

    const trackingService = useMemo(() => makeCrmSyncTrackingService({ crm, objectsType }), [crm, objectsType]);

    const handleToggle = (newIsEnabled: boolean) => {
        if (newIsEnabled) {
            setIsCrmListEnabled(newIsEnabled);
            return;
        }

        setIsConfirmDisableModalOpen(true);
    };

    const setIsCrmListEnabled = (newIsEnabled: boolean) => {
        trackingService.trackIsCrmListEnabledChange(newIsEnabled);
        dispatch(config[crm as Integration].setIsCrmListEnabledThunk(newIsEnabled));
    };

    const handleConfirmDisable = () => {
        setIsCrmListEnabled(false);
        setIsConfirmDisableModalOpen(false);
    };

    const handleCancelDisable = () => {
        setIsConfirmDisableModalOpen(false);
    };

    const handleSelect = (value: CRMListObjectsType) => {
        trackingService.trackObjectsTypeChange(value);
        dispatch(config[crm as Integration].setCrmListObjectsTypeThunk(value));
    };

    const getDropdownProps = () => {
        const crmListObjectsTypes = getCrmListObjectsTypes(crm as Integration);

        if (crmListObjectsTypes.length < 2) {
            return undefined;
        }

        return {
            options: config[crm as Integration].options.filter((option) => crmListObjectsTypes.includes(option.value)),
            value: objectsType,
            onSelect: handleSelect,
        };
    };

    return (
        <>
            <IntegrationSetting
                dataAutomation={`${crm}-crm-sync-setting`}
                title={translate("si.manage-integration.crm-sync.title", {
                    crm: getCrmName(crm as Integration),
                })}
                content={translate(`si.manage-integration.crm-sync.description.${crm}`)}
                onToggle={handleToggle}
                isEnabled={isDynamics ? false : isEnabled}
                isDisabled={isDynamics}
                dropdownProps={getDropdownProps()}
                badge={isDynamics ? <Badge type="coming soon" /> : undefined}
                badgeType={isDynamics ? undefined : "new"}
            />
            <ConfirmDisableModal
                isOpen={isConfirmDisableModalOpen}
                onConfirm={handleConfirmDisable}
                onCancel={handleCancelDisable}
            />
        </>
    );
};
